/**
 * 排行榜场景：两个视图（C 键切换）。
 * - 榜单：↑↓ 选择成绩行，←→ 难度过滤，Shift+←→ 角色过滤，Z 播放所选录像
 *   （版本不符拒播），附当前过滤的汇总行。
 * - 统计：角色 × 难度 矩阵（最高分 / 场次 / 通关数），GET /api/stats。
 * 加载失败（服务器未启动等）显示错误信息，不阻塞返回。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { cycleIndex, menuNav } from '../engine/menu.js';
import { CHARACTERS } from '../content/characters.js';
import { DIFFICULTIES } from '../content/difficulties.js';
import { t, tf, charName, diffName, getLang } from '../i18n.js';
import { REPLAY_VERSION, isReplayCompatible } from '../replay.js';

const DIFF_COLORS = ['#6cf', '#6f6', '#fc3', '#f66'];
/** 排行榜难度维度：4 档常规 + EXTRA（Extra 模式独立榜） */
const BOARD_DIFFS = [...DIFFICULTIES, { id: 'extra', name: 'Extra', cnName: 'EXTRA' }];
const BOARD_COLORS = [...DIFF_COLORS, '#f6c'];

/** 角色显示名按语言：英文 romaji，中文 cnName。 */
function charLabel(id) {
  const c = CHARACTERS.find((x) => x.id === id);
  return c ? charName(c) : String(id || '?');
}

/** 紧凑分数：中文 ≥1亿 → x.x亿、≥1万 → x万；其他语言 ≥1M → x.xM、≥1K → xK。 */
function compact(v) {
  if (getLang() !== 'zh') {
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${Math.round(v / 1e3)}K`;
    return String(v);
  }
  if (v >= 1e8) return `${(v / 1e8).toFixed(1)}亿`;
  if (v >= 1e4) return `${Math.round(v / 1e4)}万`;
  return String(v);
}

export const LeaderboardScene = {
  mgr: null,

  enter() {
    this.view = 'board'; // 'board' | 'stats'
    this.diffIdx = 1;    // 难度必选（无总榜），直接索引 BOARD_DIFFS；默认 Normal
    this.charIdx = 0;    // 0 = 全部角色，1..8 = CHARACTERS[i-1]
    this.selIdx = 0;     // 成绩行光标
    this.scores = null;
    this.stats = null;
    this.error = null;
    this.playError = null;
    this.mgr.shared.audio.playBgm('bgm_title');
    this._fetchStats();
    this._fetchScores();
  },

  exit() {},

  _fetchScores() {
    const d = BOARD_DIFFS[this.diffIdx].id;
    const c = this.charIdx === 0 ? 'all' : CHARACTERS[this.charIdx - 1].id;
    this.scores = null;
    const token = (this._fetchToken = (this._fetchToken || 0) + 1); // 防过滤器连打时慢响应后到覆盖
    fetch(`/api/scores?limit=20&difficulty=${d}&character=${c}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (token !== this._fetchToken) return;
        this.scores = data.scores || [];
        this.error = null; // 成功后必须清错误（否则永久卡在错误页——历史 bug）
      })
      .catch((e) => {
        if (token !== this._fetchToken) return;
        this.error = String((e && e.message) || e);
        this.scores = [];
      });
  },

  _fetchStats() {
    fetch('/api/stats')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        this.stats = data.stats || {};
      })
      .catch(() => {
        this.stats = null; // 统计加载失败不阻塞榜单
      });
  },

  /** 当前过滤条件下的汇总（从 stats 矩阵即时计算；难度必选，始终按难度过滤）。 */
  _summary() {
    if (!this.stats) return null;
    let count = 0;
    let best = 0;
    let clears = 0;
    const did0 = BOARD_DIFFS[this.diffIdx].id;
    for (const [cid, row] of Object.entries(this.stats)) {
      if (this.charIdx > 0 && cid !== CHARACTERS[this.charIdx - 1].id) continue;
      for (const [did, cell] of Object.entries(row)) {
        if (did !== did0) continue;
        count += cell.count;
        if (cell.best > best) best = cell.best;
        clears += cell.clears;
      }
    }
    return { count, best, clears };
  },

  /** 播放所选成绩的录像（元数据有版本先校验，不符拒播）。 */
  async _playSelected() {
    const s = this.scores && this.scores[this.selIdx];
    if (!s || !s.replayId) {
      this.playError = t('lb.noReplay');
      return;
    }
    if (s.replayVersion !== undefined && !isReplayCompatible({ version: s.replayVersion })) {
      this.playError = tf('lb.replayOld', { v: s.replayVersion, cur: REPLAY_VERSION });
      return;
    }
    try {
      const res = await fetch(`/api/replays/${s.replayId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      if (!isReplayCompatible(payload)) {
        this.playError = tf('lb.replayOld', { v: payload.version, cur: REPLAY_VERSION });
        return;
      }
      if (this.mgr.current !== this) return; // 等待 fetch 期间用户已离开（历史 bug：被强拉回游戏）
      this.mgr.shared.audio.playSfx('select');
      this.mgr.switchTo('game', { replay: payload });
    } catch (e) {
      this.playError = String((e && e.message) || e);
    }
  },

  update() {
    const audio = this.mgr.shared.audio;
    if (Input.pressed('cancel')) {
      audio.playSfx('select');
      this.mgr.switchTo('title');
      return;
    }
    if (Input.pressed('special')) { // C：切换 榜单/统计
      this.view = this.view === 'board' ? 'stats' : 'board';
      audio.playSfx('cursor');
      return;
    }
    if (this.view === 'board' && Input.pressed('confirm')) { // Z：播放所选录像
      this._playSelected();
      return;
    }
    let filterChanged = false;
    // ←→ 难度；Shift+←→ 角色（音效统一在 filterChanged 收尾处播，故不用 valueDir）
    const charShift = Input.isDown('slow');
    if (Input.pressed('left')) {
      if (charShift) {
        this.charIdx = cycleIndex(this.charIdx, CHARACTERS.length + 1, -1);
      } else {
        this.diffIdx = cycleIndex(this.diffIdx, BOARD_DIFFS.length, -1);
      }
      filterChanged = true;
    }
    if (Input.pressed('right')) {
      if (charShift) {
        this.charIdx = cycleIndex(this.charIdx, CHARACTERS.length + 1, 1);
      } else {
        this.diffIdx = cycleIndex(this.diffIdx, BOARD_DIFFS.length, 1);
      }
      filterChanged = true;
    }
    // ↑↓ 成绩行光标
    const n = this.scores ? this.scores.length : 0;
    if (this.view === 'board' && n > 0) {
      const nav = menuNav(Input, audio, this.selIdx, n);
      if (nav !== null) {
        this.selIdx = nav;
        this.playError = null;
      }
    }
    if (filterChanged) {
      audio.playSfx('cursor');
      this.selIdx = 0;
      this.playError = null;
      // stats 视图下改过滤也要重取：否则切回 board 看到的是旧条件数据（审计发现）
      this._fetchScores();
    }
  },

  draw(r) {
    r.text(t('lb.title'), VIEW_W / 2, 24, { size: 22, color: '#fff', align: 'center' });
    r.text(t('lb.hint'), VIEW_W / 2, 52, { size: 11, color: '#889', align: 'center' });

    // 过滤器（两个视图共用显示；难度必选，只展示分榜；难度/角色名按语言）
    const diffLabel = diffName(BOARD_DIFFS[this.diffIdx]);
    const charFilterLabel = this.charIdx === 0 ? t('lb.allChars') : charName(CHARACTERS[this.charIdx - 1]);
    r.text(`←→ ${diffLabel}`, 100, 78, { size: 13, color: BOARD_COLORS[this.diffIdx], align: 'center' });
    r.text(`⇧←→ ${charFilterLabel}`, VIEW_W - 110, 78, { size: 13, color: '#ccd', align: 'center' });

    if (this.error) {
      r.text(`${t('lb.loadFail')}${this.error}`, VIEW_W / 2, 200, { size: 13, color: '#f88', align: 'center' });
      r.text(t('lb.noServer'), VIEW_W / 2, 226, { size: 11, color: '#889', align: 'center' });
      return;
    }

    if (this.view === 'stats') this._drawStats(r);
    else this._drawBoard(r);
  },

  _drawBoard(r) {
    // 汇总行
    const sum = this._summary();
    if (sum) {
      r.text(tf('lb.summary', { count: sum.count, best: compact(sum.best), clears: sum.clears }), VIEW_W / 2, 100, { size: 11, color: '#8cf', align: 'center' });
    }

    if (!this.scores) {
      r.text(t('lb.loading'), VIEW_W / 2, 200, { size: 14, color: '#8cf', align: 'center' });
      return;
    }
    if (this.scores.length === 0) {
      r.text(t('lb.empty'), VIEW_W / 2, 200, { size: 14, color: '#8cf', align: 'center' });
      return;
    }

    // 表头
    const y0 = 122;
    r.text(t('lb.hRank'), 26, y0, { size: 11, color: '#8cf' });
    r.text(t('lb.hName'), 52, y0, { size: 11, color: '#8cf' });
    r.text(t('lb.hScore'), 180, y0, { size: 11, color: '#8cf' });
    r.text(t('lb.hChar'), 268, y0, { size: 11, color: '#8cf' });
    r.text(t('lb.hDiff'), 330, y0, { size: 11, color: '#8cf' });
    r.text(t('lb.hSt'), 392, y0, { size: 11, color: '#8cf' });
    r.text(t('lb.hClear'), 420, y0, { size: 11, color: '#8cf' });

    for (let i = 0; i < this.scores.length; i++) {
      const s = this.scores[i];
      const y = y0 + 18 + i * 24;
      const sel = i === this.selIdx;
      if (sel) r.rect(18, y - 4, VIEW_W - 36, 20, { fill: '#112', stroke: '#ff4' });
      const color = i === 0 ? '#fc3' : i < 3 ? '#ff4' : '#ccd';
      const di = BOARD_DIFFS.findIndex((d) => d.id === s.difficulty);
      r.text(String(i + 1), 26, y, { size: 12, color });
      r.text(String(s.name).slice(0, 10), 52, y, { size: 12, color });
      r.text(String(s.score), 180, y, { size: 12, color });
      r.text(charLabel(s.character), 268, y, { size: 12, color });
      r.text(di >= 0 ? diffName(BOARD_DIFFS[di]) : '?', 330, y, { size: 12, color: di >= 0 ? BOARD_COLORS[di] : '#889' });
      r.text(String(s.stageReached), 392, y, { size: 12, color });
      r.text(s.cleared ? '★' : '-', 426, y, { size: 12, color: s.cleared ? '#fc3' : '#667' });
      // 录像标记：版本匹配 ▶ 可播；版本不符 ✕（与无录像区分）；无录像不标
      if (s.replayId) {
        const playable = s.replayVersion === undefined || isReplayCompatible({ version: s.replayVersion, mode: s.difficulty });
        if (playable) r.text('▶', 448, y, { size: 11, color: '#6cf' });
        else r.text('✕', 448, y, { size: 11, color: '#a55' });
      }
    }
    // 播放错误提示
    if (this.playError) {
      r.text(this.playError, VIEW_W / 2, VIEW_H - 30, { size: 11, color: '#f88', align: 'center' });
    }
  },

  _drawStats(r) {
    if (!this.stats) {
      r.text(t('lb.statsLoading'), VIEW_W / 2, 200, { size: 14, color: '#8cf', align: 'center' });
      return;
    }
    // 矩阵：行 = 角色，列 = 难度（含 EXTRA）；单元 = 最高分（场次/通关）
    const x0 = 110;
    const colW = 72;
    const y0 = 130;
    const rowH = 52;

    for (let j = 0; j < BOARD_DIFFS.length; j++) {
      r.text(diffName(BOARD_DIFFS[j]), x0 + j * colW + colW / 2 - 8, y0 - 22, { size: 12, color: BOARD_COLORS[j], align: 'center' });
    }
    let anyRow = false;
    for (let i = 0; i < CHARACTERS.length; i++) {
      const cid = CHARACTERS[i].id;
      if (this.charIdx > 0 && cid !== CHARACTERS[this.charIdx - 1].id) continue;
      anyRow = true;
      const y = y0 + i * rowH;
      r.text(charName(CHARACTERS[i]), 30, y + 8, { size: 12, color: '#ccd' });
      const row = this.stats[cid] || {};
      for (let j = 0; j < BOARD_DIFFS.length; j++) {
        const did = BOARD_DIFFS[j].id;
        if (did !== BOARD_DIFFS[this.diffIdx].id) continue;
        const cell = row[did];
        const cx = x0 + j * colW;
        if (cell) {
          r.text(compact(cell.best), cx, y, { size: 12, color: '#ff4' });
          r.text(tf('lb.statCell', { count: cell.count, clears: cell.clears }), cx, y + 16, { size: 9, color: '#889' });
        } else {
          r.text('—', cx, y, { size: 12, color: '#445' });
        }
      }
    }
    if (!anyRow) {
      r.text(t('lb.noStats'), VIEW_W / 2, 200, { size: 13, color: '#8cf', align: 'center' });
    }
  },
};
